import campaignServiceEnhanced from './campaignServiceEnhanced';
import { supabase } from './supabase';

/**
 * Mock Execution Service
 * Simulates the actual sending of Apollo emails/sequences and incoming replies
 * for testing and portfolio demonstration purposes.
 */

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const executionService = {
    // A map to track running campaigns to prevent duplicate execution loops
    activeIntervals: {},

    /**
     * Start the mock execution loop for a campaign
     */
    startCampaignExecution: async (campaignId, userId) => {
        if (executionService.activeIntervals[campaignId]) {
            return { success: false, error: 'Campaign is already running.' };
        }

        try {
            // 1. Update status to active
            await campaignServiceEnhanced.updateCampaignStatus(campaignId, 'active', userId);

            // 2. Start the background simulation loop
            executionService.activeIntervals[campaignId] = true;
            executionService.runSimulationLoop(campaignId, userId);

            return { success: true, message: 'Campaign execution started successfully.' };
        } catch (error) {
            console.error('Failed to start execution:', error);
            return { success: false, error: 'Failed to start campaign execution.' };
        }
    },

    /**
     * Stop the mock execution loop
     */
    stopCampaignExecution: async (campaignId, userId) => {
        executionService.activeIntervals[campaignId] = false;
        await campaignServiceEnhanced.updateCampaignStatus(campaignId, 'paused', userId);
        return { success: true, message: 'Campaign paused.' };
    },

    /**
     * The background loop that pretends to send messages
     */
    runSimulationLoop: async (campaignId, userId) => {
        console.log(`[Demo Engine] Starting execution loop for campaign ${campaignId}`);

        while (executionService.activeIntervals[campaignId]) {
            try {
                // Fetch leads that haven't been contacted yet
                const { data: leadsResponse } = await campaignServiceEnhanced.getLeadsForCampaign(campaignId, 100, 0);

                if (!leadsResponse || !leadsResponse.leads) break;

                const pendingLeads = leadsResponse.leads.filter(lead => !lead.message_sent && lead.engagement_status !== 'disqualified');

                if (pendingLeads.length === 0) {
                    console.log(`[Demo Engine] No pending leads left. Completing campaign.`);
                    await campaignServiceEnhanced.updateCampaignStatus(campaignId, 'completed', userId);
                    executionService.activeIntervals[campaignId] = false;
                    break;
                }

                // Pick the first pending lead
                const targetLead = pendingLeads[0];

                // Simulate a delay representing pacing (e.g., 2 to 5 seconds for demo purposes)
                const delay = Math.floor(Math.random() * 3000) + 2000;
                await sleep(delay);

                // Make sure campaign wasn't paused during the sleep
                if (!executionService.activeIntervals[campaignId]) break;

                // Record the email as sent
                console.log(`[Demo Engine] Simulating Apollo email send to ${targetLead.full_name}`);
                await campaignServiceEnhanced.recordMessageSent(targetLead.id, campaignId, userId);

                // Roll the dice for an immediate mock reply (20% chance)
                if (Math.random() < 0.20) {
                    executionService.simulateIncomingReply(targetLead.id, campaignId, userId);
                }

            } catch (error) {
                console.error('[Demo Engine] Error in simulation loop:', error);
                await sleep(5000); // Back off on error
            }
        }
    },

    /**
     * Generates a realistic mock reply and intent analysis
     */
    simulateIncomingReply: async (leadId, campaignId, userId) => {
        // Wait a realistic amount of time before replying (demo scale: 5-15 seconds)
        const delay = Math.floor(Math.random() * 10000) + 5000;
        setTimeout(async () => {
            // Possible intents and realistic text responses
            const intents = [
                { intent: 'interested', texts: ["Sounds interesting, let's chat.", "Can you send me some more info?", "I'm free Tuesday at 2pm EST for a quick call."] },
                { intent: 'not_interested', texts: ["Not interested right now, thanks.", "We already use a competitor.", "Please remove me from your list."] },
                { intent: 'unclear', texts: ["Who else are you working with?", "How much does it cost?", "Can you clarify exactly what your AI does?"] }
            ];

            // Weight the odds: 30% interested, 50% not_interested, 20% unclear
            const rand = Math.random();
            let chosenType;
            if (rand < 0.3) chosenType = intents[0];
            else if (rand < 0.8) chosenType = intents[1];
            else chosenType = intents[2];

            const replyText = chosenType.texts[Math.floor(Math.random() * chosenType.texts.length)];

            console.log(`[Demo Engine] Simulating reply from lead ${leadId}: "${replyText}" [Intent: ${chosenType.intent}]`);

            // Record the reply in the DB so the dashboard statistics update
            await campaignServiceEnhanced.recordReply(
                leadId,
                campaignId,
                userId,
                replyText,
                chosenType.intent
            );

        }, delay);
    }
};

export default executionService;
